import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ComponentRef,
  EnvironmentInjector,
  Injector,
  ApplicationRef,
  createComponent,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ThemePalette } from '@angular/material/core';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import type {
  Map as MapLibreMap,
  MapLayerMouseEvent,
  Marker as MapLibreMarker,
  Popup as MapLibrePopup,
} from 'maplibre-gl';
import type { Feature, FeatureCollection, Point } from 'geojson';
import { loadLazyStylesheet } from 'src/app/_helpers/lazy-stylesheet.helper';

import { NodePolygon, RecordDtoWithPolyogns } from 'src/app/models/recordDto.model';
import { HeatMap } from 'src/app/models/heatmap.model';
import { LiveOption } from 'src/app/models/liveOption.model';
import { WindConeLayerRef, HeatmapLayerRef } from 'src/app/models/mapLibreLayerRef.model';
import { CustomMapBuilding, Floor } from 'src/app/models/customMapBuilding.model';
import { KMLConvertedData, KMLGroundOverlay } from 'src/app/models/kml-geojson.model';
import { MappingHistory } from 'src/app/models/mappingHistory.model';
import { MonitoringSite } from 'src/app/models/monitoringSite.model';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { ParameterLookup } from 'src/app/models/parameter.model';

import { CommonService } from 'src/app/services/common.service';
import {
  MapTileConfigService,
  MapTileProvider,
  MapTileSourceConfig,
  MapViewMode,
} from 'src/app/services/map-tile-config.service';
import { MapProjectionService } from 'src/app/services/map-projection.service';
import { ToastService } from 'src/app/services/toast.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { MappingGroupService } from 'src/app/services/mapping-group.service';
import { MappingHistoryService } from 'src/app/services/mapping-history.service';
import { CustomMapBuildingService } from 'src/app/services/custom-map-building.service';
import { AirNowService } from 'src/app/services/airNow.service';
import { KMLParserService } from 'src/app/services/kml-parser.service';
import { MapLibreKMLService } from 'src/app/services/maplibre-kml.service';
import { environment } from 'src/environments/environment';

import { BaseRealtimeMap } from 'src/app/shared/maps/base-realtime-map';
import { MapMarkerComponent } from '../map-marker/map-marker.component';
import { InfoWindowComponent } from 'src/app/components/info-window/info-window.component';
import { sanitizeCssColor } from 'src/app/shared/utils/css-color.util';
import { trackById } from 'src/app/shared/utils/track-by.util';

/**
 * MapLibre GL JS implementation of the real-time map for the real-time monitoring
 * dashboard. The zones page keeps the Google renderer.
 */
@Component({
  standalone: false,
  selector: 'app-maplibre-realtime-map',
  templateUrl: './maplibre-realtime-map.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./maplibre-realtime-map.component.scss'],
})
export class MapLibreRealtimeMapComponent
  extends BaseRealtimeMap
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  @Input() showHeatmap = false;
  @Input() spinnerColor?: ThemePalette;
  @Input() spinnerDiameter = 100;
  @Input() spinnerMode: ProgressSpinnerMode = 'indeterminate';
  @Input() spinnerStrokeWidth?: number;
  @Input() spinnerValue?: number;

  @Input() nodes: RecordDtoWithPolyogns[] = [];
  @Input() heatmapData: HeatMap[] = [];
  @Input() heatmapImage!: string;
  @Input() loading = false;
  @Input() selectedNode!: RecordDtoWithPolyogns;

  @Output() openNodeDetails = new EventEmitter<number>();
  @Output() toggleHeatmapEmit = new EventEmitter<boolean>();

  liveOption!: LiveOption;
  currentDisplayedTime = '';
  mapViewMode: MapViewMode = 'map';
  readonly mapViewModes: Array<{ value: MapViewMode; label: string }> = [
    { value: 'map', label: 'Map' },
    { value: 'satellite', label: 'Satellite' },
  ];

  showFloors = false;
  floorLevels: Floor[] = [];
  selectedFloorLevel: number | null = null;
  nodesToDisplay: string[] = [];

  private selectedFloor: CustomMapBuilding;
  private initialFloorLoaded = false;
  private indoorSections: CustomMapBuilding[] = [];
  private indoorSectionsSubscription?: Subscription;
  private readonly indoorKmlLayers: Array<{
    id: string;
    floor: number;
    sourceId: string;
    hitLayerIds: string[];
    onClick: () => void;
  }> = [];

  private monitoringSites: MonitoringSite[] = [];
  private showAirNowSensorsEnabled = false;
  private airNowClickBound = false;
  private airNowPopup?: MapLibrePopup;
  private readonly onAirNowSiteClick = (event: MapLayerMouseEvent): void => {
    const feature = event.features?.[0];
    if (!feature || feature.geometry.type !== 'Point') return;
    const [lng, lat] = feature.geometry.coordinates as [number, number];
    const siteId = (feature.properties as Record<string, unknown>)?.['siteId'];
    if (typeof siteId !== 'string') return;
    this.openAirNowPopup(siteId, lat, lng);
  };
  private airNowPopupRef?: ComponentRef<InfoWindowComponent>;

  private map?: MapLibreMap;
  private maplibreModule?: typeof import('maplibre-gl');
  private readonly workerBlobUrls: string[] = [];
  private isMapLoaded = false;
  private pendingMapUpdate = false;
  private pendingCenterMap = true;
  private isDestroyed = false;

  private readonly destroy$ = new Subject<void>();
  private readonly subscriptions: Subscription[] = [];
  private readonly timeoutIds: ReturnType<typeof setTimeout>[] = [];

  private readonly markers = new Map<
    string,
    { componentRef: ComponentRef<MapMarkerComponent>; marker: MapLibreMarker }
  >();
  private readonly attachedMarkerIds = new Set<string>();

  private windCones: WindConeLayerRef[] = [];
  private windConeSequence = 0;
  private windConeRun = 0;
  private coneScreenLength = 0;
  /* Every cone shares one on-screen length, scaled to the map's shorter side, so the overlay
     reads evenly instead of varying per node or per direction. */
  private readonly coneViewportRatio = 0.2;
  /* Node cards are DOM overlays painted above the canvas cones and stand above their node, so a
     cone pointing up would otherwise end under its own card; this much always shows past it. */
  private readonly minVisibleConeLength = 50;
  private heatmapCanvasLayer: HeatmapLayerRef | null = null;

  private mapTileSourceConfig!: MapTileSourceConfig;
  private satelliteTileSourceConfig!: MapTileSourceConfig;

  private readonly mapBaseLayerId = 'map-base-layer';
  private readonly satelliteBaseLayerId = 'satellite-base-layer';
  private readonly baseLayerIds = new Set(['background', this.mapBaseLayerId, this.satelliteBaseLayerId]);

  private readonly nodesSourceId = 'realtime-nodes-source';
  private readonly unclusteredLayerId = 'realtime-nodes-unclustered';
  private readonly airNowSourceId = 'airnow-sites-source';
  private readonly airNowClusterLayerId = 'airnow-sites-cluster';
  private readonly airNowClusterCountLayerId = 'airnow-sites-cluster-count';
  private readonly airNowUnclusteredLayerId = 'airnow-sites-unclustered';
  private readonly airNowIconId = 'airnow-site-icon';
  private readonly airNowClusterIconId = 'airnow-cluster-icon';
  private readonly clusterIconPixelRatio = 2;
  private readonly dustKmlSourceId = 'dust-map-kml';

  /* MapLibre's world is 512px at z0 where Google's is 256px, so the same scale sits one
     level lower here: maplibreZoom = googleZoom - 1. These keep clustering in step with
     @googlemaps/markerclusterer's defaults on the Google map (maxZoom 16, radius 60).
     That radius is not screen pixels: markerclusterer hands it to supercluster with a
     512-unit tile extent while Google draws 256px tiles, so it spans 30px on screen.
     MapLibre scales clusterRadius to true screen pixels, hence 30 here. */
  private readonly clusterMaxZoom = 15;
  private readonly clusterRadius = 30;
  private readonly clusterBadgeSize = 30;
  private readonly airNowClusterTextSize = 12;

  /* Google zoom 14 in the map this replaced. */
  private readonly fullLabelZoom = 13;

  private clusterListenersBound = false;
  private readonly clusterMarkers = new Map<number, MapLibreMarker>();
  private visibleClusterMarkers = new Map<number, MapLibreMarker>();
  private readonly refreshClusterMarkers = (): void => {
    this.updateClusteredView();
  };

  constructor(
    private commonService: CommonService,
    private tileConfigService: MapTileConfigService,
    private projectionService: MapProjectionService,
    private toastService: ToastService,
    public override dateFormatService: DateFormatService,
    private envInjector: EnvironmentInjector,
    private injector: Injector,
    private appRef: ApplicationRef,
    public override mappingGroupService: MappingGroupService,
    public override mappingHistoryService: MappingHistoryService,
    private customMapBuildingService: CustomMapBuildingService,
    private airNowService: AirNowService,
    private kmlParserService: KMLParserService,
    private kmlLayerService: MapLibreKMLService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super(mappingGroupService, mappingHistoryService, dateFormatService);
  }

  ngOnInit(): void {
    this.initializeTileProvider();
    this.loadHeaderData();

    this.subscriptions.push(
      this.commonService.nodesUpdated.subscribe((centerMap: boolean) => {
        this.updateMap(centerMap);
      }),
    );
    this.subscriptions.push(
      this.commonService.liveOptionSubject.subscribe((res) => {
        this.liveOption = res;
      }),
    );
    this.subscriptions.push(
      this.markerEvent.subscribe((centerMap: boolean) => {
        this.setMarkers(centerMap);
        this.renderDustKml();
      }),
    );
    this.subscriptions.push(
      this.windDirEvent.subscribe(() => {
        this.createWindCones();
      }),
    );
  }

  ngAfterViewInit(): void {
    this.initializeMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['nodes'] ||
      changes['showHeatmap'] ||
      changes['heatmapData'] ||
      changes['heatmapImage']
    ) {
      this.updateMap(false);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.pausePlay();
    this.indoorSectionsSubscription?.unsubscribe();
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.closeAirNowPopup();
    this.markers.forEach(({ componentRef }) => this.destroyComponent(componentRef));
    this.markers.clear();
    this.attachedMarkerIds.clear();
    this.clearHtmlClusterMarkers();
    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.workerBlobUrls.forEach((url) => URL.revokeObjectURL(url));
    this.kmlLayerService.clearAllOverlayBlobUrls();
    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
  }

  /* -------------------------------------------------- header state */

  private loadHeaderData(): void {
    this.subscriptions.push(
      this.commonService.selectParameterEmitter.subscribe((parameter: ParameterLookup) => {
        if (parameter) {
          this.selectedParameter = parameter;
          this.getUpdatedZoneData();
          this.updateMap(false);
        }
      }),
    );
    this.subscriptions.push(
      this.commonService.selectedOrgEmitter.subscribe((selectedOrg: OrganizationLevel) => {
        if (selectedOrg) {
          this.selectedOrg = selectedOrg;
          this.getUpdatedZoneData();
        }
      }),
    );
    this.subscriptions.push(
      this.commonService.selectedDateFilterEmitter.subscribe((dateFilter) => {
        if (dateFilter) {
          this.selectedDateRange = dateFilter;
          this.getUpdatedZoneData();
        }
      }),
    );
  }

  /**
   * The base class clears the previously rendered layer through the Google KmlLayer
   * handle; MapLibre removes it by source id instead.
   */
  override getUpdatedZoneData(): void {
    if (!this.activePlayOption) return;
    this.dustLayer = [];
    this.showPlayOption = false;
    this.clearDustKml();
    this.pausePlay();
    this.markerEvent.next(true);
    this.getZoneRiskData();
  }

  /* -------------------------------------------------- map lifecycle */

  private initializeTileProvider(): void {
    const config = environment as { mapTileProvider?: MapTileProvider; mapTilerApiKey?: string };
    const configuredProvider = config.mapTileProvider;
    const mapProvider =
      configuredProvider === 'usgs-satellite' ? 'openstreetmap' : configuredProvider;

    this.mapTileSourceConfig = this.tileConfigService.getTileSourceForProvider(
      mapProvider,
      config.mapTilerApiKey,
    );
    this.satelliteTileSourceConfig =
      this.tileConfigService.getTileSourceForProvider('usgs-satellite');
    this.mapViewMode = configuredProvider === 'usgs-satellite' ? 'satellite' : 'map';
  }

  /**
   * Re-hosts the worker module (and the shared chunk it imports) as blob URLs carrying an
   * explicit `text/javascript` type, so the browser's module MIME check passes regardless of
   * the `Content-Type` the static host sends for `.mjs`. Falls back to the plain asset URL if
   * the re-host itself fails, so a fetch error here degrades rather than blocks map init.
   */
  private async resolveWorkerUrl(): Promise<string> {
    const workerHref = new URL('assets/maplibre/maplibre-gl-worker.mjs', document.baseURI).href;
    const sharedHref = new URL('assets/maplibre/maplibre-gl-shared.mjs', document.baseURI).href;

    try {
      const [workerSource, sharedSource] = await Promise.all([
        fetch(workerHref).then((res) => res.text()),
        fetch(sharedHref).then((res) => res.text()),
      ]);

      const sharedImportLiteral = '"./maplibre-gl-shared.mjs"';
      if (!workerSource.includes(sharedImportLiteral)) {
        throw new Error('maplibre-gl worker no longer imports the expected shared chunk');
      }

      const sharedBlobUrl = URL.createObjectURL(
        new Blob([sharedSource], { type: 'text/javascript' }),
      );
      const patchedWorkerSource = workerSource.replace(
        sharedImportLiteral,
        `"${sharedBlobUrl}"`,
      );
      const workerBlobUrl = URL.createObjectURL(
        new Blob([patchedWorkerSource], { type: 'text/javascript' }),
      );

      this.workerBlobUrls.push(sharedBlobUrl, workerBlobUrl);
      return workerBlobUrl;
    } catch {
      return workerHref;
    }
  }

  private async initializeMap(): Promise<void> {
    try {
      const [maplibreGL] = await Promise.all([import('maplibre-gl'), loadLazyStylesheet('maplibre-gl-styles')]);
      this.maplibreModule = maplibreGL;

      /* MapLibre derives its worker URL from `import.meta.url`, which resolves to the bundled
         chunk rather than the package directory, so the module worker 404s and every GeoJSON
         source (clusters, wind cones, KML vectors) silently never loads. Point it at the copy
         angular.json places under assets instead, re-hosted as a blob so the deployed static
         host's `.mjs` MIME type (some serve it as application/octet-stream, which browsers
         refuse to run as a module worker) can't break it. */
      maplibreGL.setWorkerUrl(await this.resolveWorkerUrl());

      if (!this.mapContainer || this.isDestroyed) {
        this.toastService.displayErrorMessage('Failed to load the map', true);
        return;
      }

      const style = this.tileConfigService.buildDualRasterStyle(
        this.mapTileSourceConfig,
        this.satelliteTileSourceConfig,
        this.mapViewMode,
      );

      this.map = new maplibreGL.Map({
        container: this.mapContainer.nativeElement,
        style,
        center: [0, 0],
        zoom: 14,
        bearing: 0,
        pitch: 0,
      });

      this.addMapControls(maplibreGL);

      /* `load` only fires once every tile in the initial viewport has resolved or exhausted
         its retries, so a throttled/unreachable tile provider (a rate-limited OSM host, a
         slow network, a blocked request) can leave it pending indefinitely — stalling node
         markers, wind cones, and indoor floors along with it, none of which actually need a
         rendered base tile to exist. `style.load` fires once the style and its sources are
         parsed, independent of tile network status, so map setup no longer waits on it. */
      this.map.on('style.load', () => this.onMapLoad());
      this.map.on('error', () => {
        this.toastService.displayErrorMessage('Failed to load the map', true);
      });
      this.map.on('zoomend', () => this.updateLabelStyles());
      this.map.on('zoomend', () => void this.createWindCones());
      this.map.on('resize', () => this.redrawWindConesIfLengthChanged());
    } catch {
      this.toastService.displayErrorMessage('Failed to load the map', true);
    }
  }

  /**
   * Matches the Google map controls: compass + fullscreen top-right, and bottom-right the scale
   * with zoom buttons above it (Google's camera control). Bottom-corner controls stack upwards
   * in the order they are added, so the zoom buttons go in after the scale.
   */
  private addMapControls(maplibreGL: typeof import('maplibre-gl')): void {
    if (!this.map) return;
    this.map.addControl(
      new maplibreGL.NavigationControl({ showZoom: false, showCompass: true, visualizePitch: true }),
      'top-right',
    );
    this.map.addControl(new maplibreGL.FullscreenControl(), 'top-right');
    this.map.addControl(new maplibreGL.ScaleControl(), 'bottom-right');
    this.map.addControl(
      new maplibreGL.NavigationControl({ showZoom: true, showCompass: false }),
      'bottom-right',
    );
  }

  private onMapLoad(): void {
    this.isMapLoaded = true;
    this.applyBaseLayerVisibility();

    if (!this.initialFloorLoaded) {
      this.initialFloorLoaded = true;
      this.getIndoorSections();
    }
    if (this.showAirNowSensorsEnabled) {
      this.showAirNowSensors(true);
    }

    if (this.pendingMapUpdate) {
      this.pendingMapUpdate = false;
      const centerMap = this.pendingCenterMap;
      this.pendingCenterMap = true;
      this.updateMap(centerMap);
      return;
    }
    this.updateMap(true);
  }

  async updateMap(centerMap: boolean): Promise<void> {
    if (!this.map) return;
    if (!this.isMapLoaded) {
      this.pendingMapUpdate = true;
      this.pendingCenterMap = this.pendingCenterMap || centerMap;
      return;
    }

    this.currentDisplayedTime = this.dateFormatService.formatDateTimeWithTimeZoneToString(
      new Date(),
    );

    try {
      await this.setMarkers(centerMap);
      await this.createWindCones();
      await this.renderDustKml();

      if (this.showHeatmap) {
        await this.setHeatmap();
      } else {
        this.clearHeatmap();
      }
    } catch {
      this.toastService.displayErrorMessage('Failed to update the map', true);
    }
  }

  onMapViewChange(value: MapViewMode): void {
    this.mapViewMode = value;
    this.applyBaseLayerVisibility();
  }

  private applyBaseLayerVisibility(): void {
    if (!this.map) return;

    if (this.map.getLayer(this.mapBaseLayerId)) {
      this.map.setLayoutProperty(
        this.mapBaseLayerId,
        'visibility',
        this.mapViewMode === 'map' ? 'visible' : 'none',
      );
    }
    if (this.map.getLayer(this.satelliteBaseLayerId)) {
      this.map.setLayoutProperty(
        this.satelliteBaseLayerId,
        'visibility',
        this.mapViewMode === 'satellite' ? 'visible' : 'none',
      );
    }
  }

  /* -------------------------------------------------- extended map options */

  onShowNodesChange(showNodes: boolean): void {
    this.showNodes = showNodes;
    this.updateMap(false);
  }

  onShowWindDirChange(showWindDir: boolean): void {
    this.showWindDir = showWindDir;
    this.windDirEvent.next(showWindDir);
  }

  onZoneRiskTypeChange(riskType: string): void {
    this.activePlayOption = riskType;
    this.getZoneRiskData();
    if (this.dustLayer.length > 0) {
      this.showKmlFile(this.currentKmlIndex != null ? this.currentKmlIndex : this.dustLayer.length - 1);
    }
  }

  onZoneChange(): void {
    this.dustLayer = [];
    this.showPlayOption = false;
    this.clearDustKml();
    this.pausePlay();
    this.getZoneRiskData();
  }

  toggleHeatmap(): void {
    this.toggleHeatmapEmit.emit(true);
  }

  /* -------------------------------------------------- node markers */

  private getVisibleNodes(): Array<{ node: RecordDtoWithPolyogns; coordinates: [number, number] }> {
    return (this.showNodes ? this.nodes : [])
      .filter((node) => this.nodesToDisplay.includes(node.endPointId))
      .map((node) => {
        const coordinates = this.getNodeCoordinates(node);
        return coordinates ? { node, coordinates } : null;
      })
      .filter(
        (value): value is { node: RecordDtoWithPolyogns; coordinates: [number, number] } =>
          value !== null,
      );
  }

  private async setMarkers(centerMap: boolean): Promise<void> {
    this.markers.forEach(({ marker, componentRef }) => {
      marker.remove();
      this.destroyComponent(componentRef);
    });
    this.markers.clear();
    this.attachedMarkerIds.clear();

    const visibleNodes = this.getVisibleNodes();

    if (visibleNodes.length === 0) {
      this.clearClusterLayer();
      return;
    }

    this.syncClusterLayer(visibleNodes);

    if (centerMap) {
      this.fitToNodes();
    }

    for (const { node, coordinates } of visibleNodes) {
      this.createMarker(node, coordinates);
    }

    this.updateClusteredView();
    this.updateLabelStyles();
  }

  private fitToNodes(): void {
    const coordinates = this.nodes
      .map((node) => this.getNodeCoordinates(node))
      .filter((value): value is [number, number] => value !== null);
    const bounds = this.projectionService.getBounds(coordinates);
    if (!bounds || !this.map) return;

    this.map.fitBounds(
      [
        [bounds.west, bounds.south],
        [bounds.east, bounds.north],
      ],
      { padding: 50, maxZoom: 18 },
    );
  }

  private createMarker(
    node: RecordDtoWithPolyogns,
    coordinates: [number, number],
  ): void {
    if (!this.map || !this.maplibreModule) return;

    try {
      const componentRef = createComponent(MapMarkerComponent, {
        environmentInjector: this.envInjector,
        elementInjector: this.injector,
      });

      componentRef.instance.id = this.nodes.findIndex((n) => n.endPointId === node.endPointId);
      componentRef.instance.endPointName = node.endPointName;
      componentRef.instance.markerType = 'location';
      componentRef.instance.parameterColor = this.getMarkerColorCode(node);
      componentRef.instance.parameterValue = this.getMarkerValue(node);
      componentRef.instance.parameterType = this.selectedParameter?.parameterType ?? null;
      componentRef.instance.windSpeed = node?.windSpeed;
      componentRef.instance.windDirection = node?.windDir;
      this.appRef.attachView(componentRef.hostView);

      const el = componentRef.location.nativeElement as HTMLElement;
      el.style.cursor = 'pointer';

      /* Google's AdvancedMarkerElement pins the card's bottom-centre (its pointer) to the node,
         which is also the wind cone's apex; a fixed offset drifts as the card height changes. */
      const marker = new this.maplibreModule.Marker({ element: el, anchor: 'bottom' }).setLngLat(
        coordinates,
      );

      this.markers.set(node.endPointId as string, { componentRef, marker });

      el.addEventListener('click', () => this.onMarkerClick(node));
    } catch {
      this.toastService.displayErrorMessage('Failed to add a map marker', true);
    }
  }

  /**
   * The marker card renders the colour as `#{{parameterColor}}`, so the API hex must be
   * passed without its leading `#`; sanitising the `#`-prefixed form keeps a malformed
   * server value from breaking out of the inline style.
   */
  private getMarkerColorCode(node: RecordDtoWithPolyogns): string {
    let color = this.commonService.getColorCode(this.commonService.colors.greyDark);
    if (node?.parameterValue) {
      color =
        node?.statusId !== 0
          ? node?.parameterValue?.color ||
            this.commonService.getColorCode(this.commonService.colors.limeGreen)
          : this.commonService.getColorCode(this.commonService.colors.greyLight);
    }
    return sanitizeCssColor(`#${color}`)
      ? color
      : this.commonService.getColorCode(this.commonService.colors.greyLight);
  }

  private getMarkerValue(node: RecordDtoWithPolyogns): string {
    const hasParameterValue =
      node?.statusId !== 0 &&
      !!node?.parameterValue?.name &&
      Number.isFinite(node?.parameterValue?.value);
    if (hasParameterValue) return String(node.parameterValue.value);
    return Number.isFinite(node?.value) ? node.value.toFixed(2) : '--';
  }

  private onMarkerClick(node: RecordDtoWithPolyogns): void {
    this.selectedNode = node;
    /* Windowed cones arrive only once the dashboard loads the new node's wind distribution, so
       drop the previous node's cones now rather than leave them on the new selection. */
    if (!this.liveOption?.enabled) void this.createWindCones();
    const index = this.nodes.findIndex((n) => n.endPointId === node.endPointId);
    this.timeoutIds.push(setTimeout(() => {
      if (this.isDestroyed) return;
      this.openNodeDetails.emit(index);
      if (this.liveOption?.enabled) this.selectNode(this.selectedNode);
    }, 100));
  }

  /**
   * Centres on a node and redraws its wind cones. Called by the dashboard once the wind
   * distribution for the node has loaded, so it must not re-emit `openNodeDetails`.
   */
  selectNode(node: RecordDtoWithPolyogns): void {
    this.selectedNode = node;
    const coordinates = this.getNodeCoordinates(node);
    if (coordinates && this.map) {
      this.map.setCenter(coordinates);
    }
    this.createWindCones();
  }

  /**
   * Collapses node name labels when markers sit close together, expanding them again once
   * the map is zoomed in — mirrors the Google map behaviour.
   */
  private updateLabelStyles(): void {
    if (!this.map) return;
    const zoomLevel = this.map.getZoom();
    const thresholdDistance = 1.5; /* distance in km */
    const positions = Array.from(this.markers.values()).map(({ marker }) => marker.getLngLat());

    Array.from(this.markers.values()).forEach(({ marker }, index) => {
      const current = positions[index];
      const isNearbyLabel = positions.some(
        (other, otherIndex) =>
          otherIndex !== index &&
          this.projectionService.distanceBetweenPoints(
            current.lat,
            current.lng,
            other.lat,
            other.lng,
          ) < thresholdDistance,
      );

      const labelElement = (marker.getElement() as HTMLElement).querySelector(
        '.node-name',
      ) as HTMLElement | null;
      if (!labelElement) return;

      if (zoomLevel >= this.fullLabelZoom || !isNearbyLabel) {
        labelElement.classList.add('show-full-label');
      } else {
        labelElement.classList.remove('show-full-label');
      }
    });
  }

  /* -------------------------------------------------- clustering */

  private syncClusterLayer(
    visibleNodes: Array<{ node: RecordDtoWithPolyogns; coordinates: [number, number] }>,
  ): void {
    if (!this.map || !this.maplibreModule) return;

    const data = this.buildNodeFeatureCollection(visibleNodes);
    const source = this.map.getSource(this.nodesSourceId);

    if (this.hasGeoJsonSetData(source)) {
      source.setData(data);
    } else {
      this.map.addSource(this.nodesSourceId, {
        type: 'geojson',
        data,
        cluster: true,
        clusterMaxZoom: this.clusterMaxZoom,
        clusterRadius: this.clusterRadius,
      });
    }

    this.ensureUnclusteredLayer();
    this.ensureClusterListeners();
  }

  /**
   * A layer must reference the source for MapLibre to load its tiles (and therefore for
   * `querySourceFeatures` to report clusters); the node cards are drawn as DOM markers on
   * top, so the circles themselves stay transparent.
   */
  private ensureUnclusteredLayer(): void {
    if (!this.map || this.map.getLayer(this.unclusteredLayerId)) return;

    this.map.addLayer({
      id: this.unclusteredLayerId,
      type: 'circle',
      source: this.nodesSourceId,
      paint: {
        'circle-radius': 1,
        'circle-opacity': 0,
        'circle-stroke-width': 0,
      },
    });
  }

  private ensureClusterListeners(): void {
    if (!this.map || this.clusterListenersBound) return;
    this.map.on('moveend', this.refreshClusterMarkers);
    this.map.on('zoomend', this.refreshClusterMarkers);
    this.map.on('idle', this.refreshClusterMarkers);
    this.clusterListenersBound = true;
  }

  /**
   * Shows a cluster badge for every rendered cluster and the node card for every node that
   * is currently rendered outside a cluster.
   */
  private updateClusteredView(): void {
    if (!this.map || !this.maplibreModule || !this.map.getSource(this.nodesSourceId)) {
      this.clearHtmlClusterMarkers();
      return;
    }

    const features = this.map.querySourceFeatures(this.nodesSourceId);
    const nextVisibleMarkers = new Map<number, MapLibreMarker>();
    const unclusteredNodeIds = new Set<string>();

    for (const feature of features) {
      if (feature.geometry.type !== 'Point') continue;
      const properties = feature.properties as Record<string, unknown> | null;
      if (!properties) continue;

      if (!this.isClusterFeature(properties)) {
        const endPointId = properties['endPointId'];
        if (typeof endPointId === 'string') unclusteredNodeIds.add(endPointId);
        continue;
      }

      const clusterId = Number(properties['cluster_id']);
      const pointCount = Number(properties['point_count']);
      if (!Number.isFinite(clusterId) || !Number.isFinite(pointCount)) continue;
      if (nextVisibleMarkers.has(clusterId)) continue;

      const [lng, lat] = feature.geometry.coordinates;
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;

      let marker = this.clusterMarkers.get(clusterId);
      if (!marker) {
        const markerElement = this.createClusterBadgeElement(pointCount);
        marker = new this.maplibreModule.Marker({ element: markerElement, anchor: 'bottom' }).setLngLat([
          lng,
          lat,
        ]);
        markerElement.addEventListener('click', (event) => {
          event.stopPropagation();
          void this.zoomToCluster(clusterId, [lng, lat]);
        });
        this.clusterMarkers.set(clusterId, marker);
      } else {
        marker.setLngLat([lng, lat]);
      }

      nextVisibleMarkers.set(clusterId, marker);
      if (!this.visibleClusterMarkers.has(clusterId)) {
        marker.addTo(this.map);
      }
    }

    this.visibleClusterMarkers.forEach((marker, clusterId) => {
      if (!nextVisibleMarkers.has(clusterId)) marker.remove();
    });
    this.visibleClusterMarkers = nextVisibleMarkers;

    /* Before the first tiles resolve nothing is reported as rendered; keep every card visible. */
    const showAllMarkers = features.length === 0;
    this.markers.forEach(({ marker }, endPointId) => {
      const shouldShow = showAllMarkers || unclusteredNodeIds.has(endPointId);
      if (shouldShow === this.attachedMarkerIds.has(endPointId)) return;
      if (shouldShow) {
        marker.addTo(this.map);
        this.attachedMarkerIds.add(endPointId);
      } else {
        marker.remove();
        this.attachedMarkerIds.delete(endPointId);
      }
    });

    this.syncWindConeVisibility();
    this.redrawWindConesIfLengthChanged();
  }

  private isNodeClustered(endPointId: string): boolean {
    return this.markers.has(endPointId) && !this.attachedMarkerIds.has(endPointId);
  }

  private isClusterFeature(properties: Record<string, unknown>): boolean {
    return (
      properties['cluster'] === true ||
      properties['cluster'] === 1 ||
      properties['cluster'] === 'true'
    );
  }

  /** Same badge the Google map's MarkerClusterer renderer draws: a 30px purple gradient disc. */
  private createClusterBadgeElement(pointCount: number): HTMLDivElement {
    const element = document.createElement('div');
    const { nodeCluster, nodeClusterLight } = this.commonService.colors;
    const clusterTextColor = this.getThemeColor('--white', this.commonService.colors.white);

    element.style.width = `${this.clusterBadgeSize}px`;
    element.style.height = `${this.clusterBadgeSize}px`;
    element.style.borderRadius = '50%';
    element.style.background = `radial-gradient(circle, ${nodeCluster} 50%, ${nodeClusterLight} 100%)`;
    element.style.display = 'flex';
    element.style.alignItems = 'center';
    element.style.justifyContent = 'center';
    element.style.color = clusterTextColor;
    element.style.fontSize = '11px';
    element.style.fontWeight = '400';
    element.style.cursor = 'pointer';
    element.textContent = pointCount.toString();

    return element;
  }

  private async zoomToCluster(clusterId: number, fallbackCenter: [number, number]): Promise<void> {
    if (!this.map) return;
    const source = this.map.getSource(this.nodesSourceId);
    if (!this.hasClusterExpansionZoom(source)) {
      this.map.easeTo({ center: fallbackCenter, zoom: Math.max(this.map.getZoom() + 2, 6) });
      return;
    }

    try {
      const zoom = await source.getClusterExpansionZoom(clusterId);
      this.map.easeTo({ center: fallbackCenter, zoom });
    } catch {
      this.map.easeTo({ center: fallbackCenter, zoom: Math.max(this.map.getZoom() + 2, 6) });
    }
  }

  private clearHtmlClusterMarkers(): void {
    this.visibleClusterMarkers.forEach((marker) => marker.remove());
    this.visibleClusterMarkers.clear();
    this.clusterMarkers.forEach((marker) => marker.remove());
    this.clusterMarkers.clear();
  }

  private clearClusterLayer(): void {
    if (!this.map) return;

    if (this.clusterListenersBound) {
      this.map.off('moveend', this.refreshClusterMarkers);
      this.map.off('zoomend', this.refreshClusterMarkers);
      this.map.off('idle', this.refreshClusterMarkers);
      this.clusterListenersBound = false;
    }
    this.clearHtmlClusterMarkers();
    if (this.map.getLayer(this.unclusteredLayerId)) {
      this.map.removeLayer(this.unclusteredLayerId);
    }
    if (this.map.getSource(this.nodesSourceId)) {
      this.map.removeSource(this.nodesSourceId);
    }
  }

  private buildNodeFeatureCollection(
    visibleNodes: Array<{ node: RecordDtoWithPolyogns; coordinates: [number, number] }>,
  ): FeatureCollection<Point> {
    const features: Array<Feature<Point>> = visibleNodes.map(({ node, coordinates }) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates },
      properties: {
        endPointId: node.endPointId,
        endPointName: node.endPointName,
      },
    }));

    return { type: 'FeatureCollection', features };
  }

  /* -------------------------------------------------- AIR Now sensors */

  showAirNowSensors(isShow = true): void {
    this.showAirNowSensorsEnabled = isShow;

    if (!isShow) {
      this.clearAirNowLayers();
      return;
    }
    if (!this.map || !this.isMapLoaded) return;

    if (this.monitoringSites.length > 0) {
      this.renderAirNowSites();
      return;
    }
    this.airNowService
      .getMonitoringSites()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.monitoringSites = res ?? [];
          if (this.showAirNowSensorsEnabled) this.renderAirNowSites();
        },
        error: (error) => this.toastService.showError(error),
      });
  }

  private async renderAirNowSites(): Promise<void> {
    if (!this.map || !this.maplibreModule) return;

    const data: FeatureCollection<Point> = {
      type: 'FeatureCollection',
      features: this.monitoringSites
        .filter((site) => Number.isFinite(site.latitude) && Number.isFinite(site.longitude))
        .map((site) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [site.longitude, site.latitude] },
          properties: { siteId: site.id, name: site.name },
        })),
    };

    const source = this.map.getSource(this.airNowSourceId);
    if (this.hasGeoJsonSetData(source)) {
      source.setData(data);
      return;
    }

    await this.ensureAirNowIcon();
    this.ensureAirNowClusterIcon();

    this.map.addSource(this.airNowSourceId, {
      type: 'geojson',
      data,
      cluster: true,
      clusterMaxZoom: this.clusterMaxZoom,
      clusterRadius: this.clusterRadius,
    });

    /* Google's markers stand on their position (bottom-anchored), so the badge and its count
       sit half a badge above the cluster point. */
    this.map.addLayer({
      id: this.airNowClusterLayerId,
      type: 'symbol',
      source: this.airNowSourceId,
      filter: ['has', 'point_count'],
      layout: {
        'icon-image': this.airNowClusterIconId,
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
      },
    });

    this.map.addLayer({
      id: this.airNowClusterCountLayerId,
      type: 'symbol',
      source: this.airNowSourceId,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-size': this.airNowClusterTextSize,
        'text-offset': [0, -this.clusterBadgeSize / 2 / this.airNowClusterTextSize],
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': this.getThemeColor('--white', this.commonService.colors.white),
      },
    });

    this.map.addLayer({
      id: this.airNowUnclusteredLayerId,
      type: 'symbol',
      source: this.airNowSourceId,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': this.airNowIconId,
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
      },
    });

    if (!this.airNowClickBound) {
      this.map.on('click', this.airNowUnclusteredLayerId, this.onAirNowSiteClick);
      this.airNowClickBound = true;
    }
  }

  private ensureAirNowClusterIcon(): void {
    if (!this.map || this.map.hasImage(this.airNowClusterIconId)) return;
    const image = this.createAirNowClusterImage();
    if (image) {
      this.map.addImage(this.airNowClusterIconId, image, { pixelRatio: this.clusterIconPixelRatio });
    }
  }

  /**
   * Paints the Google map's AirNow cluster badge, which a circle layer cannot express: a 30px
   * disc filled with `radial-gradient(circle, …50%, …100%)`. CSS sizes that gradient to the
   * box's farthest corner, so the disc edge falls partway into the second stop.
   */
  private createAirNowClusterImage(): ImageData | null {
    const size = this.clusterBadgeSize * this.clusterIconPixelRatio;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (!context) return null;

    const centre = size / 2;
    const gradient = context.createRadialGradient(centre, centre, 0, centre, centre, centre * Math.SQRT2);
    gradient.addColorStop(0.5, this.commonService.colors.airNowCluster);
    gradient.addColorStop(1, this.commonService.colors.airNowClusterLight);
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(centre, centre, centre, 0, 2 * Math.PI);
    context.fill();

    return context.getImageData(0, 0, size, size);
  }

  private async ensureAirNowIcon(): Promise<void> {
    if (!this.map || this.map.hasImage(this.airNowIconId)) return;
    try {
      const image = await this.map.loadImage('assets/images/green_triangle.png');
      if (!this.map.hasImage(this.airNowIconId)) {
        this.map.addImage(this.airNowIconId, image.data);
      }
    } catch {
      this.toastService.displayErrorMessage('Failed to load the map', true);
    }
  }

  private openAirNowPopup(siteId: string, latitude: number, longitude: number): void {
    if (!this.map || !this.maplibreModule) return;
    this.closeAirNowPopup();

    const componentRef = createComponent(InfoWindowComponent, {
      environmentInjector: this.envInjector,
      elementInjector: this.injector,
    });
    componentRef.instance.siteId = siteId;
    componentRef.instance.latitude = latitude;
    componentRef.instance.longitude = longitude;
    this.appRef.attachView(componentRef.hostView);
    this.airNowPopupRef = componentRef;

    this.airNowPopup = new this.maplibreModule.Popup({ closeOnClick: true })
      .setLngLat([longitude, latitude])
      .setDOMContent(componentRef.location.nativeElement)
      .addTo(this.map);
    this.airNowPopup.on('close', () => this.closeAirNowPopup());
  }

  private closeAirNowPopup(): void {
    this.airNowPopup?.remove();
    this.airNowPopup = undefined;
    this.destroyComponent(this.airNowPopupRef);
    this.airNowPopupRef = undefined;
  }

  private clearAirNowLayers(): void {
    if (!this.map) return;
    this.closeAirNowPopup();
    if (this.airNowClickBound) {
      this.map.off('click', this.airNowUnclusteredLayerId, this.onAirNowSiteClick);
      this.airNowClickBound = false;
    }
    [
      this.airNowUnclusteredLayerId,
      this.airNowClusterCountLayerId,
      this.airNowClusterLayerId,
    ].forEach((layerId) => {
      if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
    });
    if (this.map.getSource(this.airNowSourceId)) {
      this.map.removeSource(this.airNowSourceId);
    }
  }

  /* -------------------------------------------------- dust / zoned risk KML */

  private async renderDustKml(): Promise<void> {
    if (!this.map) return;

    const hasSelection = this.dustLayer.length > 0 && this.currentKmlIndex != null;
    this.clearDustKml();
    if (!hasSelection) {
      if (this.currentKmlIndex == null) this.isPlaying = false;
      return;
    }

    const entry = this.dustLayer[this.currentKmlIndex];
    const url =
      this.activePlayOption === 'Dust Map' ? entry?.mapResultKmlFileUrl : entry?.zonedResultKmlFileUrl;
    if (!url) return;

    await this.addKmlToMap(url, this.dustKmlSourceId);
  }

  private clearDustKml(): void {
    if (!this.map) return;
    if (this.map.getSource(this.dustKmlSourceId)) {
      this.kmlLayerService.removeKMLLayer(this.map, this.dustKmlSourceId);
    }
    for (let index = 0; this.map.getSource(`${this.dustKmlSourceId}-overlay-${index}`); index++) {
      this.kmlLayerService.removeKMLLayer(this.map, `${this.dustKmlSourceId}-overlay-${index}`);
    }
  }

  private addKmlToMap(url: string, sourceId: string): Promise<KMLConvertedData | null> {
    return new Promise((resolve) => {
      this.kmlParserService
        .parseKMLFromUrl(url)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (kmlData) => {
            try {
              if (this.map && !this.isDestroyed && kmlData) {
                await this.kmlLayerService.addKMLDataToMap(this.map, kmlData, sourceId);
                /* GeoJSON has no raster concept, so a KML whose only content is a
                   GroundOverlay converts to nothing and the floorplan just stops appearing.
                   Fail loudly instead of rendering an empty map (DASH-3062 KML risk). */
                if (!kmlData.overlays?.length && !kmlData.vectorData?.features?.length) {
                  this.toastService.displayErrorMessage('Failed to load the map', true);
                }
              }
            } catch {
              this.toastService.displayErrorMessage('Failed to load the map', true);
            }
            resolve(kmlData ?? null);
          },
          error: () => {
            this.toastService.displayErrorMessage('Failed to load the map', true);
            resolve(null);
          },
        });
    });
  }

  /* -------------------------------------------------- indoor floor plans */

  /**
   * Refresh the indoor floorplans for the selected org.
   *
   * This runs on every live-mode data refresh (every 60s), so it must not throw
   * away the floor the user is looking at. When the floorplans coming back are
   * the ones already rendered we leave the KML layers and the floor picker alone
   * and only recompute which nodes are visible. A rebuild happens only when the
   * floorplans themselves changed, and even then each building is restored to the
   * floor it was showing rather than being forced back to floor 0.
   */
  getIndoorSections(): void {
    if (!this.initialFloorLoaded) return;
    /* Seed from the floors already on screen rather than from every node, so the
       markers do not flash the hidden floors while the request is in flight. */
    this.applyIndoorNodeVisibility(this.indoorSections);
    if (!this.selectedOrg?.id) return;

    /* A refresh supersedes any request still in flight. Holding a single handle
       stops the 60s live refresh from stacking a new subscription every minute. */
    this.indoorSectionsSubscription?.unsubscribe();
    this.indoorSectionsSubscription = this.customMapBuildingService
      .getAllMaps(this.selectedOrg.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const sections = res ?? [];
          if (this.isSameIndoorSections(sections)) {
            this.indoorSections = sections;
            this.applyIndoorNodeVisibility(sections);
            this.timeoutIds.push(setTimeout(() => {
              if (this.isDestroyed) return;
              this.setMarkers(false);
              this.createWindCones();
            }, 100));
            return;
          }

          const previousFloors = new Map(
            this.indoorKmlLayers.map((layer): [string, number] => [layer.id, layer.floor]),
          );
          const previousBuildingId = this.selectedFloor?.id;
          this.indoorSections = sections;
          [...this.indoorKmlLayers].forEach((layer) => this.clearKmlLayer(layer.id));
          this.nodesToDisplay = this.nodes.map((node) => node.endPointId);

          sections.forEach((area) => {
            const wanted = previousFloors.get(area.id) ?? 0;
            const layer =
              area.floors.find((floor) => floor.floor === wanted) ??
              area.floors.find((floor) => floor.floor === 0);
            if (layer) this.changeFloorLevel(layer, area);
          });

          this.restoreFloorPicker(sections, previousBuildingId);
        },
        error: (error) => this.toastService.showError(error),
      });
  }

  /**
   * True when the floorplans returned by the API are the ones already rendered,
   * so there is nothing to tear down and rebuild.
   */
  private isSameIndoorSections(sections: CustomMapBuilding[]): boolean {
    const describe = (buildings: CustomMapBuilding[]): string =>
      JSON.stringify(
        buildings
          .map((area) => ({
            id: area.id,
            floors: (area.floors ?? [])
              .map((floor) => ({
                floor: floor.floor,
                kmlFileUrl: floor.kmlFileUrl,
                nodeIds: [...(floor.nodeIds ?? [])].sort(),
              }))
              .sort((a, b) => a.floor - b.floor),
          }))
          .sort((a, b) => a.id.localeCompare(b.id)),
      );
    return describe(this.indoorSections) === describe(sections);
  }

  /**
   * Recompute nodesToDisplay from the floors that are currently rendered, so a
   * refresh picks up newly reported nodes without revealing nodes that sit on a
   * floor the user has hidden.
   */
  private applyIndoorNodeVisibility(sections: CustomMapBuilding[]): void {
    const renderedFloor = new Map(
      this.indoorKmlLayers.map((layer): [string, number] => [layer.id, layer.floor]),
    );
    const indoorNodeIds = new Set<string>();
    const visibleNodeIds = new Set<string>();
    sections.forEach((area) => {
      const shown = renderedFloor.get(area.id);
      (area.floors ?? []).forEach((floor) =>
        (floor.nodeIds ?? []).forEach((id) => {
          indoorNodeIds.add(id);
          if (floor.floor === shown) visibleNodeIds.add(id);
        }),
      );
    });
    this.nodesToDisplay = this.nodes
      .map((node) => node.endPointId)
      .filter((id) => !indoorNodeIds.has(id) || visibleNodeIds.has(id));
  }

  /**
   * Point the floor picker back at the building the user had open. If that
   * building no longer exists the picker closes, as it did before the refresh.
   */
  private restoreFloorPicker(sections: CustomMapBuilding[], previousBuildingId?: string): void {
    if (!previousBuildingId) return;
    const building = sections.find((area) => area.id === previousBuildingId);
    if (!building) {
      this.showFloors = false;
      this.floorLevels = [];
    } else {
      this.selectedFloor = building;
      this.floorLevels = building.floors;
      this.selectedFloorLevel =
        this.indoorKmlLayers.find((layer) => layer.id === building.id)?.floor ??
        this.selectedFloorLevel;
    }
    this.changeDetectorRef.detectChanges();
  }

  changeFloorLevel(layer: Floor, area?: CustomMapBuilding): void {
    area = area ?? this.selectedFloor;
    if (!area) return;

    this.clearKmlLayer(area.id);
    area.floors.forEach((floor) =>
      floor.nodeIds.forEach((id) => {
        const current = this.nodesToDisplay.indexOf(id);
        if (current !== -1) this.nodesToDisplay.splice(current, 1);
      }),
    );

    this.timeoutIds.push(setTimeout(() => {
      if (this.isDestroyed) return;
      this.nodesToDisplay = [...this.nodesToDisplay, ...layer.nodeIds];
      this.setMarkers(false);
      this.createWindCones();
    }, 100));

    this.selectedFloorLevel = layer.floor;
    const sourceId = `floor-kml-${area.id}`;
    this.indoorKmlLayers.push({
      id: area.id,
      floor: layer.floor,
      sourceId,
      hitLayerIds: [],
      onClick: () => undefined,
    });
    void this.renderFloorKml(layer, area, sourceId);
  }

  private async renderFloorKml(
    layer: Floor,
    area: CustomMapBuilding,
    sourceId: string,
  ): Promise<void> {
    if (!layer.kmlFileUrl) return;
    const kmlData = await this.addKmlToMap(layer.kmlFileUrl, sourceId);
    if (!this.map || this.isDestroyed) return;

    const onClick = (): void => {
      this.showFloors = true;
      this.selectedFloor = area;
      this.floorLevels = area.floors;
      this.changeDetectorRef.detectChanges();
    };

    const hitLayerIds: string[] = [];
    const polygonLayerId = `${sourceId}-polygons`;
    if (this.map.getLayer(polygonLayerId)) hitLayerIds.push(polygonLayerId);

    const footprintLayerId = this.addFloorFootprint(sourceId, kmlData?.overlays ?? []);
    if (footprintLayerId) hitLayerIds.push(footprintLayerId);

    hitLayerIds.forEach((hitLayerId) => this.map.on('click', hitLayerId, onClick));

    const tracked = this.indoorKmlLayers.find((entry) => entry.sourceId === sourceId);
    if (tracked) {
      tracked.hitLayerIds = hitLayerIds;
      tracked.onClick = onClick;
    }
  }

  /**
   * A GroundOverlay is a raster, and MapLibre cannot hit-test raster layers, so the floor
   * picker opens from a transparent fill traced around the overlay bounds. Vector-bearing
   * KML keeps its own footprint as an additional hit target.
   */
  private addFloorFootprint(sourceId: string, overlays: KMLGroundOverlay[]): string | null {
    if (!this.map || overlays.length === 0) return null;

    const north = Math.max(...overlays.map((overlay) => overlay.north));
    const south = Math.min(...overlays.map((overlay) => overlay.south));
    const east = Math.max(...overlays.map((overlay) => overlay.east));
    const west = Math.min(...overlays.map((overlay) => overlay.west));
    if (![north, south, east, west].every((value) => Number.isFinite(value))) return null;

    const footprintSourceId = `${sourceId}-footprint`;
    const footprintLayerId = `${footprintSourceId}-fill`;
    if (this.map.getLayer(footprintLayerId)) return footprintLayerId;

    this.map.addSource(footprintSourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [west, south],
              [east, south],
              [east, north],
              [west, north],
              [west, south],
            ],
          ],
        },
        properties: {},
      },
    });

    this.map.addLayer({
      id: footprintLayerId,
      type: 'fill',
      source: footprintSourceId,
      paint: { 'fill-opacity': 0 },
    });

    return footprintLayerId;
  }

  private clearKmlLayer(areaId: string): void {
    const index = this.indoorKmlLayers.findIndex((layer) => layer.id === areaId);
    if (index === -1) return;

    const { sourceId, hitLayerIds, onClick } = this.indoorKmlLayers[index];
    if (this.map) {
      hitLayerIds.forEach((hitLayerId) => this.map.off('click', hitLayerId, onClick));

      const footprintSourceId = `${sourceId}-footprint`;
      if (this.map.getLayer(`${footprintSourceId}-fill`)) {
        this.map.removeLayer(`${footprintSourceId}-fill`);
      }
      if (this.map.getSource(footprintSourceId)) {
        this.map.removeSource(footprintSourceId);
      }
      if (this.map.getSource(sourceId)) {
        this.kmlLayerService.removeKMLLayer(this.map, sourceId);
      }
      for (let i = 0; this.map.getSource(`${sourceId}-overlay-${i}`); i++) {
        this.kmlLayerService.removeKMLLayer(this.map, `${sourceId}-overlay-${i}`);
      }
    }
    this.indoorKmlLayers.splice(index, 1);
  }

  /* -------------------------------------------------- wind cones */

  private async createWindCones(): Promise<void> {
    /* Zoom, live updates and node selection can redraw concurrently; only the latest run draws. */
    const run = ++this.windConeRun;
    this.clearWindCones();
    if (!this.showWindDir) return;
    const screenLength = (this.coneScreenLength = this.getConeScreenLength());

    if (!this.liveOption?.enabled) {
      if (!this.selectedNode) return;
      const node = this.selectedNode;
      for (const polygon of node.polygons ?? []) {
        const shownColor =
          sanitizeCssColor(polygon?.color ? `#${polygon.color}` : '') ||
          this.commonService.colors.limeGreen;
        const windOpacity = Math.min(Math.max((polygon.windFrequency / 100) * 2, 0.15), 0.5);
        await this.addWindCone(node, shownColor, windOpacity, false, screenLength, polygon);
        if (run !== this.windConeRun) return;
      }
      return;
    }

    for (const node of this.nodes) {
      if (
        node &&
        node.statusId !== 0 &&
        this.nodesToDisplay.includes(node.endPointId) &&
        node.windDir != null
      ) {
        const shownColor =
          sanitizeCssColor(node?.parameterValue?.color ? `#${node.parameterValue.color}` : '') ||
          this.commonService.colors.limeGreen;
        await this.addWindCone(node, shownColor, 0.3, true, screenLength);
        if (run !== this.windConeRun) return;
      }
    }
  }

  private async addWindCone(
    node: RecordDtoWithPolyogns,
    color: string,
    opacity: number,
    hideWhenClustered: boolean,
    screenLength: number,
    polygon?: NodePolygon,
  ): Promise<void> {
    if (!this.map) return;
    const coordinates = this.getNodeCoordinates(node);
    if (!coordinates) return;

    const [originLng, originLat] = coordinates;
    const coneLength = this.screenLengthToMetres(originLng, originLat, screenLength);

    const bearing1 = (polygon ? polygon.windAngleSpread1 : node.windDir + 15) + 180;
    const bearing2 = (polygon ? polygon.windAngleSpread2 : node.windDir - 15) + 180;

    const [lng1, lat1] = this.projectionService.computeOffset(
      originLat,
      originLng,
      coneLength,
      bearing1,
    );
    const [lng2, lat2] = this.projectionService.computeOffset(
      originLat,
      originLng,
      coneLength,
      bearing2,
    );

    const polygonCoordinates: [number, number][] = [
      [originLng, originLat],
      [lng1, lat1],
      [lng2, lat2],
      [originLng, originLat],
    ];

    const sourceId = `wind-cone-${this.windConeSequence++}`;
    const layerId = `${sourceId}-layer`;
    const strokeLayerId = `${sourceId}-stroke`;
    const beforeLayerId = this.getWindConeBeforeLayerId();

    this.map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [polygonCoordinates] },
        properties: {},
      },
    });

    const visible = !hideWhenClustered || !this.isNodeClustered(node.endPointId);
    const layout = { visibility: visible ? 'visible' : 'none' } as const;

    this.map.addLayer({
      id: layerId,
      type: 'fill',
      source: sourceId,
      layout,
      paint: { 'fill-color': color, 'fill-opacity': opacity },
    }, beforeLayerId);

    this.map.addLayer({
      id: strokeLayerId,
      type: 'line',
      source: sourceId,
      layout,
      paint: { 'line-color': color, 'line-width': 2, 'line-opacity': opacity },
    }, beforeLayerId);

    this.windCones.push({
      sourceId,
      layerId,
      strokeLayerId,
      endPointId: node.endPointId,
      hideWhenClustered,
      visible,
    });
  }

  /**
   * Node cards are the primary layer at site-level zoom; a live cone only surfaces once its
   * node has broken out of a cluster, so a count badge is never buried under cones.
   */
  private syncWindConeVisibility(): void {
    if (!this.map) return;
    for (const cone of this.windCones) {
      if (!cone.hideWhenClustered) continue;
      const visible = !this.isNodeClustered(cone.endPointId);
      if (visible === cone.visible) continue;
      cone.visible = visible;
      [cone.layerId, cone.strokeLayerId].forEach((layerId) => {
        if (this.map.getLayer(layerId)) {
          this.map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
        }
      });
    }
  }

  /**
   * Cones are a secondary overlay, as on the Google map: they sit directly above the base
   * tiles and below every other overlay (floor plans, site footprints, dust KML, AirNow
   * sites, heatmap) instead of being appended on top of them on every refresh.
   */
  private getWindConeBeforeLayerId(): string | undefined {
    if (!this.map) return undefined;
    const coneLayerIds = new Set(
      this.windCones.flatMap((cone) => [cone.layerId, cone.strokeLayerId]),
    );
    const layerIds = this.map.getStyle().layers.map((layer) => layer.id);
    const lastBaseIndex = layerIds.reduce(
      (last, id, index) => (this.baseLayerIds.has(id) ? index : last),
      -1,
    );
    if (lastBaseIndex === -1) return undefined;
    return layerIds.slice(lastBaseIndex + 1).find((id) => !coneLayerIds.has(id));
  }

  private clearWindCones(): void {
    if (!this.map) return;
    for (const cone of this.windCones) {
      if (this.map.getLayer(cone.layerId)) this.map.removeLayer(cone.layerId);
      if (this.map.getLayer(cone.strokeLayerId)) this.map.removeLayer(cone.strokeLayerId);
      if (this.map.getSource(cone.sourceId)) this.map.removeSource(cone.sourceId);
    }
    this.windCones = [];
  }

  /* Zoom redraws cones before the cluster refresh re-attaches the node cards they must clear,
     and a resize changes the viewport they scale with, so both re-measure afterwards. */
  private redrawWindConesIfLengthChanged(): void {
    if (!this.windCones.length) return;
    if (Math.round(this.getConeScreenLength()) === Math.round(this.coneScreenLength)) return;
    void this.createWindCones();
  }

  private getConeScreenLength(): number {
    if (!this.map) return this.minVisibleConeLength;
    const container = this.map.getContainer();
    const viewportLength =
      Math.min(container.clientWidth, container.clientHeight) * this.coneViewportRatio;
    return Math.max(viewportLength, this.getLargestCardClearance() + this.minVisibleConeLength);
  }

  /**
   * Farthest a cone can run from its node while still under a node card: the distance from the
   * card's anchor to its farther top corner, which is where an upward cone finally emerges.
   */
  private getLargestCardClearance(): number {
    let clearance = 0;
    for (const id of this.attachedMarkerIds) {
      const host = this.markers.get(id)?.componentRef.location.nativeElement as HTMLElement;
      const card = host?.querySelector('.location-popup-inner');
      if (!card) continue;
      const anchor = host.getBoundingClientRect();
      const box = card.getBoundingClientRect();
      const anchorX = anchor.left + anchor.width / 2;
      const halfWidth = Math.max(anchorX - box.left, box.right - anchorX);
      clearance = Math.max(clearance, Math.hypot(halfWidth, anchor.bottom - box.top));
    }
    return clearance;
  }

  /** Ground distance, in metres, that `screenLength` pixels span at this point and zoom. */
  private screenLengthToMetres(lng: number, lat: number, screenLength: number): number {
    if (!this.map) return 0;
    const origin = this.map.project([lng, lat]);
    const edge = this.map.unproject([origin.x + screenLength, origin.y]);
    return this.projectionService.distanceBetweenPoints(lat, lng, edge.lat, edge.lng) * 1000;
  }

  /* -------------------------------------------------- heatmap */

  private async setHeatmap(): Promise<void> {
    this.clearHeatmap();

    if (!this.heatmapImage || !this.heatmapData || this.heatmapData.length === 0) return;

    try {
      const heatmapCoordinates: [number, number][] = this.heatmapData.map((h) => [
        parseFloat(h.lng.toString()),
        parseFloat(h.lat.toString()),
      ]);
      const bounds = this.projectionService.getBounds(heatmapCoordinates);
      if (!bounds) return;

      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 512;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.onerror = () => {
        this.toastService.displayErrorMessage('Failed to render the heatmap overlay', true);
      };
      img.onload = () => {
        if (!this.map || this.isDestroyed) return;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const sourceId = 'heatmap-overlay';
        if (!this.map.getSource(sourceId)) {
          this.map.addSource(sourceId, {
            type: 'image',
            url: canvas.toDataURL(),
            coordinates: [
              [bounds.west, bounds.north],
              [bounds.east, bounds.north],
              [bounds.east, bounds.south],
              [bounds.west, bounds.south],
            ],
          });

          this.map.addLayer({
            id: 'heatmap-layer',
            type: 'raster',
            source: sourceId,
            paint: { 'raster-opacity': 0.4 },
          });

          this.heatmapCanvasLayer = { sourceId, layerId: 'heatmap-layer' };
        }
      };

      img.src = 'data:image/jpeg;base64,' + this.heatmapImage;
    } catch {
      this.toastService.displayErrorMessage('Failed to render the heatmap overlay', true);
    }
  }

  private clearHeatmap(): void {
    if (!this.heatmapCanvasLayer || !this.map) return;

    if (this.map.getLayer(this.heatmapCanvasLayer.layerId)) {
      this.map.removeLayer(this.heatmapCanvasLayer.layerId);
    }
    if (this.map.getSource(this.heatmapCanvasLayer.sourceId)) {
      this.map.removeSource(this.heatmapCanvasLayer.sourceId);
    }
    this.heatmapCanvasLayer = null;
  }

  /* -------------------------------------------------- helpers */

  getMap(): MapLibreMap | undefined {
    return this.map;
  }

  private getNodeCoordinates(node: RecordDtoWithPolyogns): [number, number] | null {
    const lng = Number(node.lon);
    const lat = Number(node.lat);

    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

    return [lng, lat];
  }

  private getThemeColor(token: string, fallback: string): string {
    const tokenValue = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    return tokenValue || fallback;
  }

  private destroyComponent(ref?: ComponentRef<unknown>): void {
    if (!ref) return;
    this.appRef.detachView(ref.hostView);
    ref.destroy();
  }

  private hasGeoJsonSetData(
    source: ReturnType<MapLibreMap['getSource']>,
  ): source is ReturnType<MapLibreMap['getSource']> & {
    setData: (featureCollection: FeatureCollection<Point>) => void;
  } {
    return !!source && typeof (source as { setData?: unknown }).setData === 'function';
  }

  private hasClusterExpansionZoom(
    source: ReturnType<MapLibreMap['getSource']>,
  ): source is ReturnType<MapLibreMap['getSource']> & {
    getClusterExpansionZoom: (clusterId: number) => Promise<number>;
  } {
    return (
      !!source &&
      typeof (source as { getClusterExpansionZoom?: unknown }).getClusterExpansionZoom ===
        'function'
    );
  }

  public trackById = trackById;
}
